import { useMemo, useState } from 'react';
import {
  Background,
  Controls,
  Handle,
  MarkerType,
  MiniMap,
  Position,
  ReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { formatJalaliDate } from '@/lib/format';
import { formatNumber, persianDigits } from '@/features/reports/lib/format';
import { formatDecimal } from '@/features/reports/lib/format';
import { getPersianLabel, persianDisplayText } from '@/lib/persianUi';
import { fa } from '../i18n/fa';

const NODE_WIDTH = 196;
const STAGE_GAP = 244;

function FlowNode({ data }) {
  const label = data.type === 'stage'
    ? fa.stageName(data.details.code)
    : data.type === 'location'
      ? `${getPersianLabel(fa.chain.graph.locationTypes, data.details.location_kind, 'traceability.locationTypes')}: ${persianDisplayText(data.details.name, fa.chain.graph.unknownNode)}`
      : persianDisplayText(data.label, fa.chain.graph.unknownNode);
  return (
    <div className={`tr-flow-node tr-node-${data.type} tr-state-${data.state}`} dir="rtl">
      <Handle type="target" position={Position.Left} />
      <span className="tr-flow-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false">
          {data.type === 'stage' && <path d="M4 5h16v14H4zM8 9h8M8 13h6" />}
          {data.type === 'actor' && <><circle cx="12" cy="8" r="3" /><path d="M5 20c0-4 3-6 7-6s7 2 7 6" /></>}
          {data.type === 'location' && <><path d="M19 10c0 5-7 11-7 11S5 15 5 10a7 7 0 1 1 14 0Z" /><circle cx="12" cy="10" r="2" /></>}
          {data.type === 'product' && <><path d="m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Z" /><path d="m4 7.5 8 4.5 8-4.5M12 12v9" /></>}
        </svg>
      </span>
      <span className="tr-flow-label">{label}</span>
      {data.details.batch_badge && <span className="tr-flow-badge">{fa.chain.graph.registered}</span>}
      {data.details.flags?.length > 0 && (
        <span className="tr-flow-flags">
          {data.details.flags.map((code) => fa.ruleName(code)).join(' · ')}
        </span>
      )}
      <Handle type="source" position={Position.Right} />
    </div>
  );
}

const nodeTypes = { traceNode: FlowNode };

function positionNodes(graph) {
  const stageNodes = graph.nodes.filter((node) => node.type === 'stage');
  const stagePositions = new Map(stageNodes.map((node, index) => [node.id, index]));
  const actorNodes = graph.nodes.filter((node) => node.type === 'actor');
  const locationNodes = graph.nodes.filter((node) => node.type === 'location');

  return graph.nodes.map((node) => {
    let position;
    if (node.type === 'stage') {
      position = { x: stagePositions.get(node.id) * STAGE_GAP, y: 274 };
    } else if (node.type === 'actor') {
      const links = graph.edges.filter((edge) =>
        edge.kind === 'actor' && (edge.from === node.id || edge.to === node.id));
      const linkedStage = links
        .map((edge) => edge.from === node.id ? edge.to : edge.from)
        .find((id) => stagePositions.has(id));
      const order = linkedStage ? stagePositions.get(linkedStage) : 0;
      const lane = actorNodes.indexOf(node);
      position = { x: order * STAGE_GAP + lane % 2 * 35, y: 30 + Math.floor(lane / 2) * 125 };
    } else if (node.type === 'location') {
      const links = graph.edges.filter((edge) => edge.from === node.id);
      const linkedStage = links.map((edge) => edge.to).find((id) => stagePositions.has(id));
      const order = linkedStage ? stagePositions.get(linkedStage) : locationNodes.indexOf(node);
      position = { x: order * STAGE_GAP, y: 520 + locationNodes.indexOf(node) % 2 * 105 };
    } else {
      position = { x: stageNodes.length * STAGE_GAP, y: 274 };
    }

    return {
      id: node.id,
      type: 'traceNode',
      position,
      data: { ...node, details: node.details ?? {} },
      width: NODE_WIDTH,
      sourcePosition: 'right',
      targetPosition: 'left',
      draggable: false,
    };
  });
}

function flowEdges(graph) {
  return graph.edges.map((edge, index) => {
    const values = edge.labels ?? {};
    const labels = [];
    if (values.quantity_change_pct !== null && values.quantity_change_pct !== undefined) {
      labels.push(`${fa.chain.graph.quantityChange} ${formatDecimal(values.quantity_change_pct, 1)}٪`);
    }
    if (values.price_change_pct !== null && values.price_change_pct !== undefined) {
      labels.push(`${fa.chain.graph.priceChange} ${formatDecimal(values.price_change_pct, 1)}٪`);
    }
    if (values.days_elapsed !== null && values.days_elapsed !== undefined) {
      labels.push(`${persianDigits(values.days_elapsed)} ${fa.chain.graph.days}`);
    }
    const color = edge.flagged ? 'var(--red)' : 'var(--gray-dark)';
    return {
      id: `${edge.kind}:${edge.from}:${edge.to}:${index}`,
      source: edge.from,
      target: edge.to,
      type: 'smoothstep',
      label: labels.join(' · '),
      labelStyle: { fontSize: 12, fontWeight: 700, fill: color },
      labelBgStyle: { fill: 'var(--card-bg)', fillOpacity: 0.94 },
      style: {
        stroke: color,
        strokeWidth: edge.flagged ? 2.8 : 1.7,
        strokeDasharray: edge.flagged ? undefined : edge.kind === 'stage' ? undefined : '5 4',
      },
      markerEnd: { type: MarkerType.ArrowClosed, color },
      data: { flags: edge.flags },
    };
  });
}

function nodeDetails(node) {
  const details = node?.details ?? {};
  if (!node) return [];
  const rows = [];
  if (details.source) rows.push([fa.chain.graph.source, fa.sourceName(details.source)]);
  if (details.document_number) rows.push([fa.chain.graph.document, fa.chain.graph.registered]);
  if (details.date) rows.push([fa.chain.graph.date, formatJalaliDate(details.date)]);
  if (details.national_id) rows.push([fa.chain.graph.nationalId, fa.chain.graph.registered]);
  if (details.product_key) rows.push([fa.chain.graph.productKey, persianDisplayText(details.product_key, fa.chain.graph.registered)]);
  if (details.quantity !== null && details.quantity !== undefined) {
    rows.push([fa.chain.graph.quantity, `${formatNumber(details.quantity)} ${fa.chain.qty}`]);
  }
  if (details.price !== null && details.price !== undefined) {
    rows.push([fa.chain.graph.price, `${formatNumber(details.price)} ${fa.chain.perKg}`]);
  }
  if (details.batch_codes?.length) {
    rows.push([fa.chain.graph.batches, fa.chain.graph.registered]);
  }
  if (details.flags?.length) {
    rows.push([fa.chain.graph.flags, details.flags.map(fa.ruleName).join('، ')]);
  }
  if (details.note) rows.push([fa.chain.graph.note, persianDisplayText(details.note, fa.chain.graph.registered)]);
  if (details.values?.length) {
    rows.push([fa.chain.graph.locations, details.values.map((value) => persianDisplayText(value, fa.chain.graph.unknownNode)).join('، ')]);
  }
  return rows;
}

function TraceabilityGraph({ graph, loading, error }) {
  const [selection, setSelection] = useState({ chainId: null, nodeId: null });
  const nodes = useMemo(() => graph ? positionNodes(graph) : [], [graph]);
  const edges = useMemo(() => graph ? flowEdges(graph) : [], [graph]);
  const selectedNode = selection.chainId === graph?.chain_id
    ? graph?.nodes.find((node) => node.id === selection.nodeId)
    : null;
  const details = useMemo(() => nodeDetails(selectedNode), [selectedNode]);
  if (loading) return <div className="tr-graph-message">{fa.chain.graph.loading}</div>;
  if (error) return <div className="tr-graph-message tr-error">{fa.chain.graph.error}</div>;
  if (!graph) return <div className="tr-graph-message">{fa.chain.graph.unavailable}</div>;

  return (
    <div className="tr-graph-workspace">
      <div className="tr-flow-canvas" dir="ltr">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodeClick={(_event, node) => setSelection({
            chainId: graph.chain_id,
            nodeId: node.id,
          })}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.18, minZoom: 0.3, maxZoom: 0.9 }}
          minZoom={0.2}
          maxZoom={1.5}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable
          proOptions={{ hideAttribution: true }}
        >
          <Background color="#cbd5e1" gap={24} />
          <Controls position="bottom-left" />
          <MiniMap
            position="bottom-right"
            pannable
            zoomable
            nodeColor={(node) => {
              if (node.data.state === 'missing' || node.data.state === 'unverified') return '#94a3b8';
              if (node.data.state === 'flagged') return '#dc2626';
              if (node.data.type === 'actor') return '#7c3aed';
              if (node.data.type === 'location') return '#0284c7';
              if (node.data.type === 'product') return '#d97706';
              return '#16836b';
            }}
          />
        </ReactFlow>
      </div>
      <aside className="tr-graph-details" dir="rtl" aria-live="polite">
        <h3>{fa.chain.graph.detailsTitle}</h3>
        {selectedNode ? (
          <>
            <strong className="tr-graph-detail-name">
              {selectedNode.type === 'stage'
                ? fa.stageName(selectedNode.details.code)
                : selectedNode.type === 'location'
                  ? `${getPersianLabel(fa.chain.graph.locationTypes, selectedNode.details.location_kind, 'traceability.locationTypes')}: ${persianDisplayText(selectedNode.details.name, fa.chain.graph.unknownNode)}`
                : persianDisplayText(selectedNode.label, fa.chain.graph.unknownNode)}
            </strong>
            <span className={`tr-graph-state tr-state-${selectedNode.state}`}>
              {getPersianLabel(fa.chain.graph.states, selectedNode.state, 'traceability.nodeStates')}
            </span>
            {details.length > 0 ? (
              <dl>
                {details.map(([label, value]) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="tr-sub">{fa.chain.graph.noDetails}</p>
            )}
          </>
        ) : (
          <p className="tr-sub">{fa.chain.graph.selectNode}</p>
        )}
      </aside>
    </div>
  );
}

export default function ChainExplorer({
  chains,
  chain,
  onSelect,
  units,
  graph,
  graphLoading,
  graphError,
}) {
  const [view, setView] = useState('graph');
  if (!chain) return null;
  const qtyUnit = fa.unitLabel(units?.quantity_unit);
  const perKg = `${fa.unitLabel(units?.price_unit)} / ${fa.unitLabel(units?.quantity_unit)}`;
  const coverage = Math.round(chain.coverage * 100);
  return (
    <div className="tr-card tr-panel tr-chain-explorer">
      <div className="tr-chain-heading">
        <h2 className="tr-title">{fa.charts.chain}</h2>
        <div className="tr-chain-controls">
          <select
            className="tr-select"
            value={chain.id}
            onChange={(e) => onSelect(e.target.value)}
            aria-label={fa.charts.chainSelect}
          >
            {chains.map((c) => (
              <option key={c.id} value={c.id}>
                {fa.chain.numberedChain(chains.findIndex((item) => item.id === c.id) + 1)}
                {c.holder || c.actor ? ` · ${persianDisplayText(c.holder || c.actor, 'فروشنده')}` : ''}
                {c.flags.length ? ` · ${fa.chain.graph.flags} ${formatNumber(c.flags.length)}` : ''}
              </option>
            ))}
          </select>
          <div className="tr-view-toggle" role="group" aria-label={fa.chain.graph.viewLabel}>
            <button type="button" aria-pressed={view === 'graph'} onClick={() => setView('graph')}>
              {fa.chain.graph.viewGraph}
            </button>
            <button type="button" aria-pressed={view === 'list'} onClick={() => setView('list')}>
              {fa.chain.graph.viewList}
            </button>
          </div>
        </div>
      </div>
      <div className="tr-sub" style={{ marginTop: 10 }}>
        {fa.chain.coverage} {persianDigits(coverage)}٪ · {fa.chain.lastVerified}:{' '}
        <b>{fa.stageName(chain.last_verified)}</b>
      </div>
      <div className="tr-bar">
        <i style={{ width: `${coverage}%` }} />
      </div>
      {view === 'graph' ? (
        <TraceabilityGraph graph={graph} loading={graphLoading} error={graphError} />
      ) : (
        <ul className="tr-tl">
          {chain.stages.map((s) => (
            <li key={s.code}>
              <span
                className={`tr-ic ${!s.verified ? 'no' : s.bad ? 'bad' : 'ok'}`}
                role="img"
                aria-label={!s.verified ? fa.chain.graph.states.missing : s.bad ? fa.chain.graph.states.flagged : fa.chain.graph.states.verified}
              >
                <span aria-hidden="true">{!s.verified ? '–' : s.bad ? '✕' : '✓'}</span>
              </span>
              <div className="tr-stage">
                {fa.stageName(s.code)}
                <div className="tr-sub">
                  {fa.sourceName(s.source)}
                  {s.verified
                    ? ` · ${s.date ? formatJalaliDate(s.date) : fa.chain.noDate}`
                    : ` · ${fa.chain.noSourceData}`}
                </div>
              </div>
              <div className="tr-stage-val">
                {s.verified && s.qty ? `${formatNumber(s.qty)} ${qtyUnit}` : ''}
                {s.verified && s.price ? <><br />{`${formatNumber(s.price)} ${perKg}`}</> : null}
              </div>
            </li>
          ))}
          <li>
            <span className="tr-ic no">◇</span>
            <div className="tr-stage">
              {fa.chain.endCustomer}
              <div className="tr-sub">{fa.chain.endCustomerSource}</div>
            </div>
            <div className="tr-stage-val">
              {chain.end_price ? `${formatNumber(chain.end_price)} ${perKg}` : '—'}
            </div>
          </li>
        </ul>
      )}
    </div>
  );
}
